import { useEffect, useState } from "react";
import { Volume2, PenLine, Eye, Sparkles } from "lucide-react";
import { play } from "../../lib/audio";
import { markSeen, recordAttempt, say } from "../../lib/zhuyinRoom";
import { StrokeAnim } from "./Strokes";
import { TraceCanvas } from "../lesson/TraceCanvas";
import { ZhuyinCard } from "./RoomUI";

const Tab = ({ on, onClick, icon: Icon, children, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className={`h-12 px-5 rounded-full font-round text-xl flex items-center gap-2 border-2 ${on ? "bg-[#1D6FB8] text-white border-transparent" : "bg-white text-[#14507f] border-[#b9dcf0]"}`}>
    <Icon size={20} />{children}
  </button>
);

// One zhuyin: card, sound, mnemonic, stroke order and tracing.
export const LearnPanel = ({ z, childId, startTrace = false, onTraced }) => {
  const [mode, setMode] = useState(startTrace ? "trace" : "order");
  const [k, setK] = useState(0);
  const [won, setWon] = useState(false);
  useEffect(() => {
    setMode(startTrace ? "trace" : "order");
    setWon(false);
    setK((x) => x + 1);
    say(z);
    markSeen(childId, z.symbol);
  }, [z.symbol, childId, startTrace]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => {
    setWon(true);
    recordAttempt(childId, z.symbol, "room_trace", true);
    say(z);
    onTraced?.();
  };
  return (
    <div className="grid lg:grid-cols-[minmax(0,420px)_1fr] gap-8 items-start" data-testid={`learn-panel-${z.symbol}`}>
      <div className="flex flex-col gap-4 w-full max-w-[420px] mx-auto">
        <ZhuyinCard z={z} testid="learn-card" />
        <div className="flex flex-wrap justify-center gap-3">
          <button data-testid="learn-play-btn" onClick={() => say(z)} className="kid-btn px-6 bg-[#1D6FB8] text-white font-round text-xl flex items-center gap-2"><Volume2 /> 聽發音</button>
          <button data-testid="learn-mnemonic-btn" onClick={() => play(z.mnemonic)} className="kid-btn px-6 bg-white border-2 border-[#b9dcf0] text-[#14507f] font-round text-xl flex items-center gap-2"><Sparkles /> 聽口訣</button>
        </div>
      </div>
      <div className="flex flex-col items-center gap-5">
        <button data-testid="learn-symbol-btn" onClick={() => say(z)} className="flex items-end gap-4">
          <span className="text-[6rem] leading-none font-black text-[#14507f]">{z.symbol}</span>
          <span className="font-round text-xl text-stone-500 pb-3">{z.strokes?.length} 畫</span>
        </button>
        <div className="flex gap-3">
          <Tab on={mode === "order"} onClick={() => { setMode("order"); setK((x) => x + 1); }} icon={Eye} testid="learn-tab-order">看筆順</Tab>
          <Tab on={mode === "trace"} onClick={() => { setMode("trace"); setWon(false); }} icon={PenLine} testid="learn-tab-trace">描一描</Tab>
        </div>
        {mode === "order" ? (
          <button onClick={() => setK((x) => x + 1)} data-testid="learn-order-replay" className="bg-[#FFFDF7] rounded-[2rem] border-4 border-amber-200 p-2">
            <StrokeAnim strokes={z.strokes} playKey={k} className="w-[min(78vw,380px)] h-[min(78vw,380px)]" />
          </button>
        ) : (
          <TraceCanvas key={z.symbol} glyph={z.symbol} onComplete={done} testid="learn-trace" />
        )}
        {won && mode === "trace" && <div className="font-round text-2xl text-green-700" data-testid="learn-trace-done">好棒！寫對了 ⭐</div>}
        <button data-testid="learn-example-btn" onClick={() => play(z.example_word)} className="scroll-card px-6 py-3 flex items-center gap-4">
          <span className="text-5xl">{z.image_asset}</span>
          <span className="font-serif-tc text-5xl font-black text-red-950">{z.example_character}</span>
          <span className="zhuyin-vert text-2xl font-bold text-[var(--vermilion)]">{z.example_zhuyin}</span>
          <span className="font-round text-lg text-stone-500">{z.example_word}</span>
        </button>
      </div>
    </div>
  );
};
